import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { Spinner, Button, Dropdown } from "flowbite-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faRocket, 
  faChartLine, 
  faClock,
  faDownload,
  faSync,
  faPalette,
} from "@fortawesome/free-solid-svg-icons";
import { format, parse, isValid, isSameDay, isWithinInterval } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Component imports
import Navigation from "../components/Navigation";
import UsageChart from "../components/AnalyticsDashboard/Charts/UsageChart";
import EndpointsChart from "../components/AnalyticsDashboard/Charts/EndpointsChart";
import ErrorsChart from "../components/AnalyticsDashboard/Charts/ErrorsChart";
import LatencyChart from "../components/AnalyticsDashboard/Charts/LatencyChart";
import PerformanceChart from "../components/AnalyticsDashboard/Charts/PerformanceChart";
import MetricCard from "../components/AnalyticsDashboard/Cards/MetricCard";
import CalendarIcon from "../components/AnalyticsDashboard/Utilities/CalendarIcon";
import AnalyticsInfoModal from "../modals/AnalyticsInfoModal";

// Utility imports
import { generateAnalyticsData } from "../components/AnalyticsDashboard/Utilities/helpers";
import THEMES from "../components/AnalyticsDashboard/Constants/themes";

// Styles
import '../styles/AnalyticsDashboard.css';

const MotionDiv = motion.div;

export default function AnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [showComparison, setShowComparison] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEndpoints, setFilterEndpoints] = useState([]);
  const [visibleCharts, setVisibleCharts] = useState({
    usage: true,
    endpoints: true,
    errors: true,
    latency: true,
    performance: true
  });
  const [includeAnomalies, setIncludeAnomalies] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(true);

  const themeColors = THEMES[selectedTheme];

  useEffect(() => {
    const now = new Date();
    switch(timeRange) {
      case '24h':
        setStartDate(new Date(now.getTime() - 86400000));
        setEndDate(now);
        break;
      case '7d':
        setStartDate(new Date(now.getTime() - 7 * 86400000));
        setEndDate(now);
        break;
      case '30d':
        setStartDate(new Date(now.getTime() - 30 * 86400000));
        setEndDate(now);
        break;
      // 'custom' case handled by date pickers
    }
  }, [timeRange]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1200));
      const newData = generateAnalyticsData(startDate, endDate, includeAnomalies);
      setData(newData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [startDate, endDate, includeAnomalies]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshData = async () => {
    setRefreshing(true);
    await loadData();
  };

  const handleExportData = () => {
    if (!data) return;
    
    // Convert data to CSV
    const csvContent = [
      'date,requests,errors,latency',
      ...data.usage.map(day => `${day.date},${day.requests},${day.errors},${day.latency}`)
    ].join('\n');
    
    // Create a download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `api-analytics-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCustomDate = (input) => {
    try {
      const parsed = parse(input, 'dd/MM/yy', new Date());
      return isValid(parsed) ? parsed : new Date();
    } catch {
      return new Date();
    }
  };

  // Calculate metrics
  const totalRequests = data?.usage.reduce((sum, day) => sum + (day.requests || 0), 0) || 0;
  const errorRate = data?.usage 
    ? (data.usage.reduce((sum, day) => sum + (day.errors || 0), 0) / totalRequests * 100).toFixed(1)
    : 0;
  const avgLatency = data?.usage 
    ? (data.usage.reduce((sum, day) => sum + (day.latency || 0), 0) / data.usage.filter(day => day.latency !== null).length).toFixed(0)
    : 0;
    
  // Calculate previous period metrics for comparison
  const prevTotalRequests = data?.prevPeriodUsage?.reduce((sum, day) => sum + day.requests, 0) || 0;
  const requestsChange = totalRequests && prevTotalRequests 
    ? ((totalRequests - prevTotalRequests) / prevTotalRequests * 100).toFixed(1)
    : 0;

  // Filter endpoints based on search term
  const filteredEndpoints = data?.endpoints
    ? data.endpoints.filter(ep => 
        searchTerm ? ep.endpoint.toLowerCase().includes(searchTerm.toLowerCase()) : true)
    : [];
    
  // Filter Latencies based on search term
  const filteredLatencies = data?.latency
    ? data.latency.filter(c => 
        searchTerm ? c.endpoint.toLowerCase().includes(searchTerm.toLowerCase()) : true)
    : [];

  const toggleChart = (chartName) => {
    setVisibleCharts(prev => ({
      ...prev,
      [chartName]: !prev[chartName]
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div className={`analytics-dashboard flex flex-col w-full h-full min-h-screen dark:bg-gray-900 bg-[conic-gradient(at_top,_var(--tw-gradient-stops))] ${themeColors.background} dark:${themeColors.darkBackground}`}>
      <Navigation stay={true} />
      <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm pl-0 xl:pl-8 xl:pr-8 mx-4 lg:mx-20 my-24 lg:my-32 rounded-xl shadow-lg">
        <div className="container px-4 lg:px-6 py-8 lg:py-12 mx-auto">
        <MotionDiv initial="hidden" animate="visible" variants={containerVariants}>
            {/* Header Section */}
            <MotionDiv variants={itemVariants} className="mb-8">
              <div className="w-full">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  API Analytics Dashboard
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Insights into your API usage and performance metrics
                </p>
              </div>
            </MotionDiv>
            
            {/* Controls Section */}
            <MotionDiv variants={itemVariants} className="mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <Button 
                    color="light" 
                    size="sm"
                    className="bg-white dark:bg-gray-800 shadow-sm"
                    onClick={refreshData}
                    disabled={refreshing}
                  >
                    <FontAwesomeIcon icon={faSync} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  
                  <Button 
                    color="light" 
                    size="sm"
                    className="bg-white dark:bg-gray-800 shadow-sm"
                    onClick={handleExportData}
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-2" />
                    Export
                  </Button>
                  
                  <Dropdown 
                    label={
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faPalette} />
                        <span className="hidden md:inline">Theme</span>
                      </div>
                    }
                    color="light"
                    size="sm"
                    className="bg-white dark:bg-gray-800 shadow-sm"
                  >
                    <Dropdown.Item onClick={() => setSelectedTheme('default')}>
                      Default
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setSelectedTheme('ocean')}>
                      Ocean
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setSelectedTheme('sunset')}>
                      Sunset
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setSelectedTheme('forest')}>
                      Forest
                    </Dropdown.Item>
                  </Dropdown>
                </div>
                
                {/* Time Controls - Both time range selector and date picker */}
                <div className="flex flex-col lg:flex-row justify-end items-end gap-4">
                  {/* Date Range Picker - Only when custom is selected */}
                  {timeRange === 'custom' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full lg:w-auto">
                      <div className="flex items-center gap-2 w-full relative group">
                        <div className="absolute left-3 z-30">
                          <CalendarIcon />
                        </div>
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => {
                            const validDate = isValid(date) ? date : new Date();
                            setStartDate(validDate);
                            if (validDate > endDate) setEndDate(validDate);
                          }}
                          onChangeRaw={(e) => {
                            const parsed = parseCustomDate(e.target.value);
                            setStartDate(parsed);
                            if (parsed > endDate) setEndDate(parsed);
                          }}
                          dateFormat="dd/MM/yyyy"
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          maxDate={endDate}
                          className="w-full h-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm rounded-lg pl-10 pr-3 focus:ring-2 focus:ring-blue-300/50 dark:focus:ring-blue-600/30 border border-gray-200/80 dark:border-gray-700 transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-600 shadow-sm hover:shadow-md"
                          popperPlacement="bottom-start"
                          popperClassName="react-datepicker-popper animate-pop-in"
                          calendarClassName="shadow-2xl rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white dark:bg-gray-800 backdrop-blur-lg"
                          dayClassName={(date) => {
                            // Exact match check (same day, month, and year)
                            const isExactlySelected = isSameDay(date, startDate);
                            
                            // Check if date is in the currently displayed month on the calendar
                            const isDisplayedMonth = date.getMonth() === date.getMonth();
                            
                            // Precise in-range check (must be exact same day, month, and year)
                            const isInRange = (
                              isWithinInterval(date, { start: startDate, end: endDate }) && 
                              (
                                // Either exactly matches start date (same day, month, year)
                                (date.getDate() === startDate.getDate() && 
                                date.getMonth() === startDate.getMonth() && 
                                date.getFullYear() === startDate.getFullYear()) ||
                                // Or exactly matches end date (same day, month, year)
                                (date.getDate() === endDate.getDate() && 
                                date.getMonth() === endDate.getMonth() && 
                                date.getFullYear() === endDate.getFullYear()) ||
                                // Or is a day between start and end date in exact same month
                                (date.getMonth() === startDate.getMonth() && 
                                date.getFullYear() === startDate.getFullYear() && 
                                date > startDate && date < endDate) ||
                                (date.getMonth() === endDate.getMonth() && 
                                date.getFullYear() === endDate.getFullYear() && 
                                date > startDate && date < endDate)
                              )
                            );
                            
                            if (isExactlySelected) {
                              return "bg-blue-500 text-white hover:bg-blue-600 rounded-md";
                            } else if (isInRange) {
                              return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-md";
                            } else if (!isDisplayedMonth) {
                              return "text-gray-400 dark:text-gray-600";
                            }
                            return "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md";
                          }}
                          placeholderText="Start Date"
                          // Custom styles for calendar elements
                          calendarContainer={({ className, children }) => (
                            <div className={`${className} dark:text-white`}>{children}</div>
                          )}
                          weekDayClassName={() => "text-gray-500 dark:text-gray-400 font-medium"} 
                          monthClassName={() => "font-bold text-gray-800 dark:text-white"}
                          renderCustomHeader={({
                            date,
                            changeYear,
                            changeMonth,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled
                          }) => (
                            <div className="flex items-center justify-between px-2 py-2">
                              <button
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                                type="button"
                              >
                                <span className="sr-only">Previous Month</span>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                {format(date, "MMMM yyyy")}
                              </div>
                              <button
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                                type="button"
                              >
                                <span className="sr-only">Next Month</span>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </div>
                          )}
                        />
                      </div>
                      
                      <div className="flex items-center gap-2 w-full relative group">
                        <div className="absolute left-3 z-30">
                          <CalendarIcon />
                        </div>
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => {
                            const validDate = isValid(date) ? date : new Date();
                            setEndDate(validDate);
                            if (validDate < startDate) setStartDate(validDate);
                          }}
                          onChangeRaw={(e) => {
                            const parsed = parseCustomDate(e.target.value);
                            setEndDate(parsed);
                            if (parsed < startDate) setStartDate(parsed);
                          }}
                          dateFormat="dd/MM/yyyy"
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate}
                          maxDate={new Date()}
                          className="w-full h-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm rounded-lg pl-10 pr-3 focus:ring-2 focus:ring-blue-300/50 dark:focus:ring-blue-600/30 border border-gray-200/80 dark:border-gray-700 transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-600 shadow-sm hover:shadow-md"
                          popperPlacement="bottom-start"
                          popperClassName="react-datepicker-popper animate-pop-in"
                          calendarClassName="shadow-2xl rounded-xl border border-gray-200/50 dark:border-gray-700/50 bg-white dark:bg-gray-800 backdrop-blur-lg"
                          dayClassName={(date) => {
                            // Exact match check (same day, month, and year)
                            const isExactlySelected = isSameDay(date, endDate);
                            
                            // Check if date is in the currently displayed month on the calendar
                            const isDisplayedMonth = date.getMonth() === date.getMonth();
                            
                            // Precise in-range check (must be exact same day, month, and year)
                            const isInRange = (
                              isWithinInterval(date, { start: startDate, end: endDate }) && 
                              (
                                // Either exactly matches start date (same day, month, year)
                                (date.getDate() === startDate.getDate() && 
                                date.getMonth() === startDate.getMonth() && 
                                date.getFullYear() === startDate.getFullYear()) ||
                                // Or exactly matches end date (same day, month, year)
                                (date.getDate() === endDate.getDate() && 
                                date.getMonth() === endDate.getMonth() && 
                                date.getFullYear() === endDate.getFullYear()) ||
                                // Or is a day between start and end date in exact same month
                                (date.getMonth() === startDate.getMonth() && 
                                date.getFullYear() === startDate.getFullYear() && 
                                date > startDate && date < endDate) ||
                                (date.getMonth() === endDate.getMonth() && 
                                date.getFullYear() === endDate.getFullYear() && 
                                date > startDate && date < endDate)
                              )
                            );
                            
                            // Future date check (disable future dates)
                            const isFuture = date > new Date();
                            
                            if (isExactlySelected) {
                              return "bg-blue-500 text-white hover:bg-blue-600 rounded-md";
                            } else if (isInRange) {
                              return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-md";
                            } else if (isFuture) {
                              return "text-gray-300 dark:text-gray-600 cursor-not-allowed";
                            } else if (!isDisplayedMonth) {
                              return "text-gray-400 dark:text-gray-600";
                            }
                            return "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md";
                          }}
                          placeholderText="End Date"
                          // Custom styles for calendar elements
                          calendarContainer={({ className, children }) => (
                            <div className={`${className} dark:text-white`}>{children}</div>
                          )}
                          weekDayClassName={() => "text-gray-500 dark:text-gray-400 font-medium"} 
                          monthClassName={() => "font-bold text-gray-800 dark:text-white"}
                          renderCustomHeader={({
                            date,
                            changeYear,
                            changeMonth,
                            decreaseMonth,
                            increaseMonth,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled
                          }) => (
                            <div className="flex items-center justify-between px-2 py-2">
                              <button
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                                type="button"
                              >
                                <span className="sr-only">Previous Month</span>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                {format(date, "MMMM yyyy")}
                              </div>
                              <button
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                                type="button"
                              >
                                <span className="sr-only">Next Month</span>
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Time Range Selector */}
                  <div className="w-full md:w-40">
                    <select
                      value={timeRange}
                      onChange={(e) => setTimeRange(e.target.value)}
                      className="w-full h-10 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-2 focus:ring-blue-300/50 dark:focus:ring-blue-600/30 pl-4 pr-8 py-2 border border-gray-200/80 dark:border-gray-700 transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-600 shadow-sm hover:shadow-md appearance-none"
                    >
                      <option value="24h">Last 24h</option>
                      <option value="7d">Last 7d</option>
                      <option value="30d">Last 30d</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
              </div>
            </MotionDiv>

            {/* Chart Toggle Options */}
            <MotionDiv variants={itemVariants} className="mb-6">
              <div className="flex flex-wrap gap-3">
                <Button.Group>
                  {Object.entries(visibleCharts).map(([chart, isVisible]) => (
                    <Button 
                    key={chart}
                    color={isVisible ? "light" : "gray"} 
                    size="xs"
                    onClick={() => toggleChart(chart)}
                    className={`
                      ${isVisible 
                        ? "bg-blue-50 text-blue-600 border border-blue-200 font-medium dark:bg-gray-700 dark:text-white dark:border-gray-600" 
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}
                    `}
                  >
                    {chart.charAt(0).toUpperCase() + chart.slice(1)}
                  </Button>
                  ))}
                </Button.Group>
                
                <Button
                  color="light"
                  size="xs"
                  onClick={() => setShowComparison(!showComparison)}
                  className={showComparison ? "bg-white dark:bg-gray-700" : ""}
                >
                  {showComparison ? "Hide Comparison" : "Show Comparison"}
                </Button>
                
                <Button
                  color="light"
                  size="xs"
                  onClick={() => setIncludeAnomalies(!includeAnomalies)}
                  className={includeAnomalies ? "bg-white dark:bg-gray-700" : ""}
                >
                  {includeAnomalies ? "Normal View" : "Highlight Anomalies"}
                </Button>
              </div>
            </MotionDiv>

            {loading ? (
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <Spinner aria-label="Loading analytics..." size="xl" />
                  <p className="mt-4 text-gray-500 dark:text-gray-400">Loading your analytics data...</p>
                </div>
              </div>
            ) : (
              <MotionDiv variants={containerVariants} className="space-y-8">
                <AnimatePresence mode="wait">
                  <MotionDiv 
                    variants={itemVariants} 
                    className="grid grid-cols-1 md:grid-cols-3 gap-6"
                    key="metrics"
                  >
                    <MetricCard
                      icon={faRocket}
                      title="Total Requests"
                      value={totalRequests.toLocaleString()}
                      trend={parseFloat(requestsChange)}
                      color={themeColors.primary}
                      previousValue={showComparison ? prevTotalRequests.toLocaleString() : null}
                    />
                    <MetricCard
                      icon={faChartLine}
                      title="Success Rate"
                      value={`${(100 - errorRate)}%`}
                      trend={2.1}
                      color={themeColors.secondary}
                      previousValue={showComparison ? "96.8%" : null}
                    />
                    <MetricCard
                      icon={faClock}
                      title="Avg. Latency"
                      value={`${avgLatency}ms`}
                      trend={-5.7}
                      color={themeColors.warning}
                      previousValue={showComparison ? "198ms" : null}
                    />
                  </MotionDiv>
                </AnimatePresence>

                {visibleCharts.usage && (
                  <AnimatePresence mode="wait">
                    <MotionDiv 
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      key="usage-chart"
                    >
                      <UsageChart 
                        data={data}
                        timeRange={timeRange}
                        themeColors={themeColors}
                        showComparison={showComparison}
                        includeAnomalies={includeAnomalies}
                              />
                    </MotionDiv>
                  </AnimatePresence>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {visibleCharts.endpoints && (
                    <AnimatePresence mode="wait">
                      <MotionDiv 
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        key="endpoints-chart"
                      >
                        <EndpointsChart
                              data={filteredEndpoints}
                          themeColors={themeColors}
                              />
                      </MotionDiv>
                    </AnimatePresence>
                  )}

                  {visibleCharts.errors && (
                    <AnimatePresence mode="wait">
                      <MotionDiv 
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        key="errors-chart"
                      >
                        <ErrorsChart
                                data={data?.errors}
                          themeColors={themeColors}
                                  />
                      </MotionDiv>
                    </AnimatePresence>
                  )}

                  {visibleCharts.latency && (
                    <AnimatePresence mode="wait">
                      <MotionDiv 
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        key="latency-chart"
                      >
                        <LatencyChart
                              data={filteredLatencies}
                          themeColors={themeColors}
                        />
                      </MotionDiv>
                    </AnimatePresence>
                  )}

                  {visibleCharts.performance && (
                    <AnimatePresence mode="wait">
                      <MotionDiv 
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        key="performance-chart"
                      >
                        <PerformanceChart
                          data={data?.performanceMetrics}
                          themeColors={themeColors}
                        />
                      </MotionDiv>
                    </AnimatePresence>
                  )}
                </div>
              </MotionDiv>
            )}
          </MotionDiv>
        </div>
      </div>
      <AnalyticsInfoModal show={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </div>
  );
}